import { BleDevice } from '@capacitor-community/bluetooth-le';

import { ESPConstants } from './ESPConstants';
import { Session } from './Session';
import { WiFiAccessPoint } from './WiFiAccessPoint';
import { Security } from './security/Security';
import { Security0 } from './security/Security0';
import { Security1 } from './security/Security1';
import { BLETransport } from './transport/BLETransport';
import { Transport } from './transport/Transport';
import { Convert } from './utils/Convert';
import { MessageHelper } from './utils/MessageHelper';

const Constants = require('./proto/constants_pb');
const Provisioning = require('./proto/provisioning_pb');
const WifiScan = require('./proto/wifi_scan_pb');
const WifiConfig = require('./proto/wifi_config_pb');
const WifiConstants = require('./proto/wifi_constants_pb');

const textDecoder = new TextDecoder();

type ProvisioningStateKeys = keyof typeof Provisioning.States;
type ProvisioningState = (typeof Provisioning.States)[ProvisioningStateKeys];

/**
 * ESPDevice class to hold device information. This will give facility to
 * connect device, send data to device and do provisioning of it.
 */
export class ESPDevice {
    private session?: Session;
    private security?: Security;
    private transport?: Transport;

    private transportType: ESPConstants.TransportType;
    private securityType: ESPConstants.SecurityType;

    private proofOfPossession = '';
    private totalCount: number = 0;
    private startIndex: number = 0;
    private wifiApList: WiFiAccessPoint[] = [];
    private bluetoothDevice?: BleDevice;
    private primaryServiceUuid?: string;
    private deviceName: string = '';
    private deviceMacAddress: string = '';

    constructor(
        transportType: ESPConstants.TransportType,
        securityType: ESPConstants.SecurityType
    ) {
        this.transportType = transportType;
        this.securityType = securityType;

        switch (transportType) {
            case ESPConstants.TransportType.TRANSPORT_BLE:
                this.transport = new BLETransport();
                break;
        }
    }

    /**
     * This method is used to connect ESPDevice.
     */
    public async connectToDevice() {
        switch (this.transportType) {
            case ESPConstants.TransportType.TRANSPORT_BLE:
                await (this.transport as BLETransport).connect(
                    this.bluetoothDevice!,
                    this.primaryServiceUuid!
                );
                break;
        }
    }

    /**
     * This method is used to connect ESPDevice using BLE transport.
     *
     * @param bluetoothDevice    BleDevice
     * @param primaryServiceUuid Primary service UUID.
     *
     * @return Returns a Promise with a void
     */
    public connectBLEDevice(
        bluetoothDevice: BleDevice,
        primaryServiceUuid?: string
    ): Promise<void> {
        if (this.transport instanceof BLETransport) {
            this.deviceName = bluetoothDevice.name || '';
            this.deviceMacAddress = bluetoothDevice.name?.substring(5) || '';
            return (this.transport as BLETransport).connect(
                bluetoothDevice,
                primaryServiceUuid
            );
        } else {
            throw new Error('Trying to connect device with wrong transport.');
        }
    }

    /**
     * This method is used to disconnect ESPDevice.
     * Note : It will disconnect only if device is connected thorough BLE transport.
     *
     * @return Returns a Promise with a void
     */
    public async disconnectDevice(): Promise<void> {
        if (this.transport instanceof BLETransport) {
            await (this.transport as BLETransport).disconnect();
        }
        this.session = undefined;
    }

    /**
     * This method is used to set Proof Of Possession.
     *
     * @param pop Proof Of Possession of the device.
     */
    public setProofOfPossession(pop: string): void {
        this.proofOfPossession = pop;
    }

    /**
     * This method is used to get Proof Of Possession.
     *
     * @return Returns Proof Of Possession of the device.
     */
    public getProofOfPossession(): string {
        return this.proofOfPossession;
    }

    /**
     * This method is used to set device name.
     *
     * @param deviceName Device name to be set.
     */
    public setDeviceName(deviceName: string): void {
        this.deviceName = deviceName;
    }

    /**
     * This method is used to get the device name.
     *
     * @return Returns device name.
     */
    public getDeviceName(): string {
        return this.deviceName;
    }

    /**
     * This method is used to set device mac address.
     *
     * @param deviceMacAddress Device mac address to be set.
     */
    public setDeviceMacAddress(deviceMacAddress: string): void {
        this.deviceMacAddress = deviceMacAddress;
    }

    /**
     * This method is used to get the device mac address.
     *
     * @return Returns device name.
     */
    public getDeviceMacAddress(): string {
        return this.deviceMacAddress;
    }

    /**
     * This method is used to get transport type.
     *
     * @return Returns transport type.
     */
    public getTransportType(): ESPConstants.TransportType {
        return this.transportType;
    }

    /**
     * This method is used to get security type.
     *
     * @return Returns security type.
     */
    public getSecurityType(): ESPConstants.SecurityType {
        return this.securityType;
    }

    /**
     * This method is used to get BleDevice object to connect with device using BLE.
     *
     * @return Returns BleDevice object of the device.
     */
    public getBluetoothDevice(): BleDevice {
        return this.bluetoothDevice!;
    }

    /**
     * This method is used to set BleDevice.
     *
     * @param bluetoothDevice BleDevice
     */
    public setBluetoothDevice(bluetoothDevice: BleDevice): void {
        this.bluetoothDevice = bluetoothDevice;
    }

    /**
     * This method is used to get primary service UUID of the BLE device.
     *
     * @return Returns Primary service UUID of the device.
     */
    public getPrimaryServiceUuid(): string {
        return this.primaryServiceUuid!;
    }

    /**
     * This method is used to set primary service UUID.
     *
     * @param primaryServiceUuid Primary service UUID of the device.
     */
    public setPrimaryServiceUuid(primaryServiceUuid: string): void {
        this.primaryServiceUuid = primaryServiceUuid;
    }

    /**
     * Send scan command to device to get available Wi-Fi access points.
     *
     * @return Returns Promise with an array of WiFiAccessPoint
     */
    public async scanNetworks(): Promise<WiFiAccessPoint[]> {
        if (!this.session || !this.session.isEstablished()) {
            await this.initSession();
        }

        return this.startNetworkScan();
    }

    /**
     * Send Wi-Fi credentials to device for provisioning.
     *
     * @param ssid       SSID of the Wi-Fi which is to be configure in device.
     * @param passphrase Password of the Wi-Fi which is to be configure in device.
     *
     * @return Returns Promise with void
     */
    public async provision(ssid: string, passphrase: string): Promise<void> {
        if (!this.session || !this.session.isEstablished()) {
            await this.initSession();
        }

        return this.sendWiFiConfig(ssid, passphrase);
    }

    /**
     * Send Macs to device for provisioning.
     *
     * @param macs A list of mac addresses which is to be configure in device.
     *
     * @return Returns Promise with void or a list of failed macs
     */
    public async provisionMacs(macs: string[]): Promise<string[]> {
        if (!this.session || !this.session.isEstablished()) {
            await this.initSession();
        }

        return this.sendMacsConfig(macs);
    }

    /**
     * Get device provisioning status.
     * This method will return the status of provisioning.
     *
     * @returns Returns Promise with a ProvisioningState
     */
    public async getDeviceState(): Promise<ProvisioningState> {
        if (!this.session || !this.session.isEstablished()) {
            await this.initSession();
        }

        return this.getDevicePropagationStatus();
    }

    /**
     * Ends the provisioning.
     *
     * @return Returns Promise with void
     */
    public async endMacProvisioning(
        clearSettings: boolean = false,
        propagateWifiPass: boolean = true
    ): Promise<void> {
        const scanCommand = MessageHelper.prepareEndProvisioningMsg(
            clearSettings,
            propagateWifiPass
        );

        try {
            const returnData: Uint8Array =
                await this.session!.sendDataToDevice(
                    ESPConstants.HANDLER_PROV_MACS,
                    scanCommand
                );

            const ok: boolean =
                this.processEndProvisioningResponse(returnData);

            if (ok) {
                this.session = undefined;
            } else {
                throw new Error('Failed to end provisioning.');
            }
        } catch (e) {
            this.session = undefined;
            throw new Error('Failed to end provisioning.');
        }
    }

    private initSession(): Promise<void> {
        if (this.securityType === ESPConstants.SecurityType.SECURITY_0) {
            this.security = new Security0();
        } else {
            this.security = new Security1(this.proofOfPossession);
        }

        this.session = new Session(this.transport!, this.security!);

        return this.session.init();
    }

    private async startNetworkScan(): Promise<WiFiAccessPoint[]> {
        this.totalCount = 0;
        this.startIndex = 0;
        this.wifiApList = [];

        let returnData: Uint8Array;

        try {
            const scanCommand: Uint8Array = MessageHelper.prepareWiFiScanMsg();

            returnData = await this.session!.sendDataToDevice(
                ESPConstants.HANDLER_PROV_SCAN,
                scanCommand
            );

            this.processStartScanResponse(returnData);

            const getScanStatusCmd: Uint8Array =
                MessageHelper.prepareGetWiFiScanStatusMsg();

            returnData = await this.session!.sendDataToDevice(
                ESPConstants.HANDLER_PROV_SCAN,
                getScanStatusCmd
            );
        } catch (e) {
            throw new Error('Failed to send Wi-Fi scan command.');
        }

        return this.processWifiStatusResponse(returnData);
    }

    private async getFullWiFiList(): Promise<WiFiAccessPoint[]> {
        console.log(
            `Total count: ${this.totalCount} and start index is: ${this.startIndex}`
        );

        let wifiList: Promise<WiFiAccessPoint[]>;

        if (this.totalCount < 4) {
            wifiList = this.getWiFiScanList(0, this.totalCount);
        } else {
            const temp: number = this.totalCount - this.startIndex;

            if (temp > 0) {
                if (temp > 4) {
                    wifiList = this.getWiFiScanList(this.startIndex, 4);
                } else {
                    wifiList = this.getWiFiScanList(this.startIndex, temp);
                }
            } else {
                console.log('Nothing to do. Wifi list completed.');
                wifiList = this.completeWifiList();
            }
        }

        return wifiList;
    }

    private async getWiFiScanList(
        start: number,
        count: number
    ): Promise<WiFiAccessPoint[]> {
        console.log(`Getting ${count} SSIDs`);

        if (count <= 0) {
            return this.completeWifiList();
        }

        const data: Uint8Array = MessageHelper.prepareGetWiFiScanListMsg(
            start,
            count
        );

        try {
            const returnData: Uint8Array =
                await this.session!.sendDataToDevice(
                    ESPConstants.HANDLER_PROV_SCAN,
                    data
                );
            console.log('Successfully got SSID list');
            return this.processGetSSIDs(returnData);
        } catch (e) {
            throw new Error('Failed to get Wi-Fi Networks.');
        }
    }

    private async completeWifiList(): Promise<WiFiAccessPoint[]> {
        return this.wifiApList;
    }

    private async sendWiFiConfig(
        ssid: string,
        passphrase: string
    ): Promise<void> {
        const scanCommand: Uint8Array = MessageHelper.prepareWiFiConfigMsg(
            ssid,
            passphrase
        );

        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_CONFIG,
            scanCommand
        );
        const status = this.processWifiConfigResponse(returnData);

        if (status !== Constants.Status.SUCCESS) {
            throw new Error('Failed to send wifi credentials to device');
        } else {
            return this.applyWiFiConfig();
        }
    }

    private async sendMacsConfig(macs: string[]): Promise<string[]> {
        const scanCommand: Uint8Array =
            MessageHelper.prepareMacsConfigMsg(macs);

        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_MACS,
            scanCommand
        );

        const status = this.processMacsConfigResponse(returnData);

        if (status !== Provisioning.State.SUCCESS) {
            throw new Error('Failed to send MACs to device');
        }

        return this.pollForMacsPropagationStatus();
    }

    private async applyWiFiConfig(): Promise<void> {
        const scanCommand: Uint8Array =
            MessageHelper.prepareApplyWiFiConfigMsg();

        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_CONFIG,
            scanCommand
        );

        const status = this.processApplyConfigResponse(returnData);

        if (status === Constants.Status.SUCCESS) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(this.pollForWifiConnectionStatus());
                }, 2000);
            });
        } else {
            throw new Error('Failed to apply wifi credentials');
        }
    }

    private async pollForWifiConnectionStatus(): Promise<void> {
        const message: Uint8Array =
            MessageHelper.prepareGetWiFiConfigStatusMsg();
        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_CONFIG,
            message
        );

        const statuses = this.processProvisioningStatusResponse(returnData);
        const wifiStationState = statuses[0];
        const failedReason = statuses[1];

        if (wifiStationState === WifiConstants.WifiStationState.CONNECTED) {
            // Provision success
            console.log('Provision success');
        } else if (
            wifiStationState === WifiConstants.WifiStationState.DISCONNECTED
        ) {
            // Device disconnected but Provision may got success / failure
            this.session = undefined;
            throw new Error('Device disconnected');
        } else if (
            wifiStationState === WifiConstants.WifiStationState.CONNECTING
        ) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(this.pollForWifiConnectionStatus());
                }, 1000);
            });
        } else {
            if (
                failedReason ===
                WifiConstants.WifiConnectFailedReason.AUTHERROR
            ) {
                throw new Error('Invalid wifi credentials');
            } else if (
                failedReason ===
                WifiConstants.WifiConnectFailedReason.NETWORKNOTFOUND
            ) {
                throw new Error('Network not found');
            } else {
                throw new Error('Unknown error');
            }
        }
    }

    private async getDevicePropagationStatus(): Promise<ProvisioningState> {
        const message: Uint8Array =
            MessageHelper.prepareGetMacsPropagationStatusMsg();
        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_MACS,
            message
        );

        const statuses = this.processMacsPropagationStatusResponse(returnData);
        const espNowState = statuses[0];
        // const macs = statuses[1] as string[];

        return new Promise((resolve) => {
            resolve(espNowState);
        });
    }

    private async pollForMacsPropagationStatus(): Promise<string[]> {
        const message: Uint8Array =
            MessageHelper.prepareGetMacsPropagationStatusMsg();
        const returnData: Uint8Array = await this.session!.sendDataToDevice(
            ESPConstants.HANDLER_PROV_MACS,
            message
        );

        const statuses = this.processMacsPropagationStatusResponse(returnData);
        const espNowState = statuses[0];
        const macs = statuses[1] as string[];

        if (espNowState === Provisioning.State.SUCCESS) {
            // Provision success
            console.log('Provision success');
            return [];
        } else if (espNowState === Provisioning.State.PROPAGATING) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(this.pollForMacsPropagationStatus());
                }, 1000);
            });
        } else {
            if (macs.length) {
                // Return failed macs
                return new Promise((resolve) => {
                    resolve(macs);
                });
            }

            throw new Error(`MACs propagation Failed`);
        }
    }

    private processStartScanResponse(responseData: Uint8Array): void {
        console.log('Process Wi-Fi start scan command response');

        try {
            const payload =
                WifiScan.WiFiScanPayload.deserializeBinary(responseData);
            const response = WifiScan.RespScanStart.deserializeBinary(
                payload.serializeBinary()
            );
            // TODO Proto should send status as ok started or failed
            console.log(response);
        } catch (e) {
            console.error(e);
        }
    }

    private processWifiStatusResponse(
        responseData: Uint8Array
    ): Promise<WiFiAccessPoint[]> {
        console.log('Process Wi-Fi scan status command response');
        const payload =
            WifiScan.WiFiScanPayload.deserializeBinary(responseData);
        const response = payload.getRespScanStatus();
        const scanFinished: boolean = response.getScanFinished();

        if (scanFinished) {
            this.totalCount = response.getResultCount();
            return this.getFullWiFiList();
        } else {
            throw new Error('Failed to get Wi-Fi status.');
        }
    }

    private async processGetSSIDs(
        responseData: Uint8Array
    ): Promise<WiFiAccessPoint[]> {
        const payload =
            WifiScan.WiFiScanPayload.deserializeBinary(responseData);
        const response = payload.getRespScanResult();
        const entriesList = response.getEntriesList();

        // console.log('Response count : ' + entriesList.length);

        for (let i = 0; i < entriesList.length; i += 1) {
            const ssid: string = textDecoder.decode(entriesList[i].getSsid());
            // console.log('SSID : ' + ssid);
            const rssi: number = entriesList[i].getRssi();
            let isAvailable: boolean = false;

            for (let index = 0; index < this.wifiApList.length; index += 1) {
                if (ssid === this.wifiApList![index].getWifiName()) {
                    isAvailable = true;

                    if (this.wifiApList[index].getRssi()! < rssi) {
                        this.wifiApList[index].setRssi(rssi);
                    }
                    break;
                }
            }

            if (!isAvailable) {
                const wifiAp = new WiFiAccessPoint();
                wifiAp.setWifiName(ssid);
                wifiAp.setRssi(entriesList[i].getRssi());
                wifiAp.setSecurity(entriesList[i].getAuth());
                this.wifiApList.push(wifiAp);
            }

            // console.log('Size of  list : ' + this.wifiApList!.length);
        }

        this.startIndex = this.startIndex! + 4;

        const temp: number = this.totalCount! - this.startIndex;

        if (temp > 0) {
            return this.getFullWiFiList();
        } else {
            // console.log('Wi-Fi LIST Completed');
            return this.completeWifiList();
        }
    }

    private processWifiConfigResponse(responseData: Uint8Array): number {
        let status = Constants.Status.INVALIDSESSION;
        const wiFiConfigPayload =
            WifiConfig.WiFiConfigPayload.deserializeBinary(responseData);
        status = wiFiConfigPayload.getRespSetConfig().getStatus();

        return status;
    }

    private processMacsConfigResponse(responseData: Uint8Array): number {
        let status = Provisioning.State.UNKNOWN;
        const wrapper = Provisioning.Wrapper.deserializeBinary(responseData);
        status = wrapper.getSetMacsResp().getState();

        return status;
    }

    private processApplyConfigResponse(responseData: Uint8Array): number {
        let status = Constants.Status.INVALIDSESSION;
        const wiFiConfigPayload =
            WifiConfig.WiFiConfigPayload.deserializeBinary(responseData);
        status = wiFiConfigPayload.getRespApplyConfig().getStatus();

        return status;
    }

    private processProvisioningStatusResponse(
        responseData: Uint8Array
    ): number[] {
        let wifiStationState = WifiConstants.WifiStationState.DISCONNECTED;
        let failedReason = -1;

        if (!responseData) {
            return [wifiStationState, failedReason];
        }

        const wiFiConfigPayload =
            WifiConfig.WiFiConfigPayload.deserializeBinary(responseData);
        wifiStationState = wiFiConfigPayload.getRespGetStatus().getStaState();
        failedReason = wiFiConfigPayload.getRespGetStatus().getFailReason();

        return [wifiStationState, failedReason];
    }

    private processMacsPropagationStatusResponse(
        responseData: Uint8Array
    ): string[][] {
        let espNowState = Provisioning.State.UNKNOWN;
        const macs: string[] = [];

        if (!responseData) {
            return [espNowState, macs];
        }

        const wrapper = Provisioning.Wrapper.deserializeBinary(responseData);
        espNowState = wrapper.getGetStateResp().getState();
        const failedMacs = wrapper.getGetStateResp().getFailedMacsList();

        for (let i = 0, l = failedMacs.length; i < l; i += 1) {
            macs.push(Convert.uint8ArrayToHexString(failedMacs[i]));
        }

        return [espNowState, macs];
    }

    private processEndProvisioningResponse(responseData: Uint8Array): boolean {
        let res = false;

        if (!responseData) {
            return false;
        }

        const wrapper = Provisioning.Wrapper.deserializeBinary(responseData);
        res = wrapper.getId() === Provisioning.ids.PKT_END_PROV_RESP;

        return res;
    }
}
